import type { ScanCallableEntity } from '@/components/smartai/api/getCallGraph';

export const BLAST_RADIUS_LARGE_CLOSURE_THRESHOLD = 200;

export type BlastRadiusByDepth = Record<
  number,
  { fanIn: string[]; fanOut: string[] }
>;

export type NaturalMaxDepth = {
  maxFanInDepth: number;
  maxFanOutDepth: number;
  maxDepth: number;
};

export type BlastRadiusResult = {
  seedEntityKey: string;
  depth: number;
  maxDepth: number;
  maxFanInDepth: number;
  maxFanOutDepth: number;
  entityKeys: Set<string>;
  nodeIds: Set<string>;
  seedNodeIds: string[];
  directFanIn: number;
  directFanOut: number;
  totalFanIn: number;
  totalFanOut: number;
  totalAffected: number;
  byDepth: BlastRadiusByDepth;
  unresolvedSkipped: number;
  isLargeClosure: boolean;
};

type GraphNodeLike = {
  id?: unknown;
  entity_type?: unknown;
  entityType?: unknown;
  entity_key?: unknown;
  entity_row_id?: unknown;
  source_file?: unknown;
};

function stringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(Boolean).map(String);
}

export function fanInKeys(ent: ScanCallableEntity): string[] {
  return stringList(ent.metadata?.fanIn);
}

export function fanOutKeys(ent: ScanCallableEntity): string[] {
  const m = ent.metadata;
  if (!m) return [];
  return stringList(m.resolvedFanout ?? m.resolvedFanouts ?? m.rawFanout);
}

function entityRowId(ent: ScanCallableEntity): string {
  const entityId = (ent.entityId ?? '').toString().trim();
  if (entityId) return entityId;
  const docId = (ent._id ?? '').toString().trim();
  if (docId) return docId;
  const ek = (ent.entityKey ?? '').toString().trim();
  const line = ent.lineStart;
  if (ek) return `${ek}:L${line ?? ''}`;
  return `${ent.type ?? 'Unknown'}:${ent.name ?? 'anonymous'}:L${line ?? ''}`;
}

function computeDirectionMaxDepth(
  seed: string,
  entitiesByKey: Map<string, ScanCallableEntity>,
  getNeighbors: (ent: ScanCallableEntity) => string[],
): number {
  if (!seed || !entitiesByKey.has(seed)) return 0;

  let maxLevel = 0;
  let frontier = [seed];
  const visited = new Set<string>([seed]);

  while (frontier.length > 0) {
    const nextFrontier: string[] = [];
    for (const key of frontier) {
      const ent = entitiesByKey.get(key);
      if (!ent) continue;
      for (const neighbor of getNeighbors(ent)) {
        if (!neighbor || visited.has(neighbor)) continue;
        if (!entitiesByKey.has(neighbor)) continue;
        visited.add(neighbor);
        nextFrontier.push(neighbor);
      }
    }
    if (nextFrontier.length === 0) break;
    maxLevel += 1;
    frontier = nextFrontier;
  }

  return maxLevel;
}

/**
 * Deepest meaningful dependency depth for dropdown cap.
 * Uses longest pure fan-in / fan-out chain, with a +1 unified hop only for
 * shallow seeds that need to reach caller/callee siblings (empty fan-out case).
 */
function computeMeaningfulMaxDepth(
  seed: string,
  entitiesByKey: Map<string, ScanCallableEntity>,
  maxFanInDepth: number,
  maxFanOutDepth: number,
): number {
  let maxDepth = Math.max(1, maxFanInDepth, maxFanOutDepth);

  if (maxDepth >= 2) {
    return maxDepth;
  }

  const emptyByDepth: BlastRadiusByDepth = { 0: { fanIn: [], fanOut: [] } };
  const atBase = expandUnifiedClosure([seed], maxDepth, entitiesByKey, emptyByDepth);
  const atPlusOne = expandUnifiedClosure(
    [seed],
    maxDepth + 1,
    entitiesByKey,
    { 0: { fanIn: [], fanOut: [] } },
  );
  if (atPlusOne.closure.size > atBase.closure.size) {
    maxDepth += 1;
  }

  return maxDepth;
}

/** Longest resolved fan-in / fan-out chain lengths for a seed method. */
export function computeNaturalMaxDepth(
  seedEntityKey: string,
  entitiesByKey: Map<string, ScanCallableEntity>,
): NaturalMaxDepth {
  const seed = seedEntityKey.trim();
  if (!seed) {
    return { maxFanInDepth: 0, maxFanOutDepth: 0, maxDepth: 1 };
  }

  const maxFanInDepth = computeDirectionMaxDepth(
    seed,
    entitiesByKey,
    fanInKeys,
  );
  const maxFanOutDepth = computeDirectionMaxDepth(
    seed,
    entitiesByKey,
    fanOutKeys,
  );

  return {
    maxFanInDepth,
    maxFanOutDepth,
    maxDepth: computeMeaningfulMaxDepth(
      seed,
      entitiesByKey,
      maxFanInDepth,
      maxFanOutDepth,
    ),
  };
}

/** Map entityKey → graph node ids from call-graph payload nodes. */
export function buildEntityKeyToNodeIds(
  nodes: GraphNodeLike[],
  entitiesByKey: Map<string, ScanCallableEntity>,
): Map<string, string[]> {
  const map = new Map<string, string[]>();

  const add = (entityKey: string, nodeId: string) => {
    if (!entityKey || !nodeId) return;
    const existing = map.get(entityKey) ?? [];
    if (!existing.includes(nodeId)) {
      map.set(entityKey, [...existing, nodeId]);
    }
  };

  for (const node of nodes) {
    const entityType = String(node.entity_type ?? node.entityType ?? '');
    if (entityType === 'File') continue;
    const nodeId = String(node.id ?? '');
    const entityKey = String(node.entity_key ?? '').trim();
    if (entityKey) {
      add(entityKey, nodeId);
    }
  }

  for (const [entityKey, ent] of entitiesByKey) {
    if (map.has(entityKey)) continue;
    const rowId = entityRowId(ent);
    const match = nodes.find(
      n =>
        String(n.entity_row_id ?? '') === rowId ||
        String(n.id ?? '') === rowId,
    );
    if (match) {
      add(entityKey, String(match.id ?? ''));
    }
  }

  return map;
}

/** Map file path → File node id. */
export function buildFilePathToNodeId(
  nodes: GraphNodeLike[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const node of nodes) {
    const entityType = String(node.entity_type ?? node.entityType ?? '');
    if (entityType !== 'File') continue;
    const fp = String(node.source_file ?? '').trim();
    const nodeId = String(node.id ?? '');
    if (fp && nodeId) map.set(fp, nodeId);
  }
  return map;
}

type UnifiedFrontierItem = {
  key: string;
  direction: 'fanIn' | 'fanOut';
  depth: number;
};

/** BFS fan-in + fan-out from seeds — mirrors backend ``_expand_fan_closure``. */
function expandUnifiedClosure(
  seeds: string[],
  depth: number,
  entitiesByKey: Map<string, ScanCallableEntity>,
  byDepth: BlastRadiusByDepth,
): {
  closure: Set<string>;
  fanInClosure: Set<string>;
  fanOutClosure: Set<string>;
  unresolvedSkipped: number;
} {
  const closure = new Set<string>();
  const fanInClosure = new Set<string>();
  const fanOutClosure = new Set<string>();
  let unresolvedSkipped = 0;
  const visited = new Set<string>(seeds.filter(key => entitiesByKey.has(key)));

  let frontier: UnifiedFrontierItem[] = [];
  for (const seed of seeds) {
    const ent = entitiesByKey.get(seed);
    if (!ent) {
      unresolvedSkipped += 1;
      continue;
    }
    for (const key of fanInKeys(ent)) {
      frontier.push({ key, direction: 'fanIn', depth: 1 });
    }
    for (const key of fanOutKeys(ent)) {
      frontier.push({ key, direction: 'fanOut', depth: 1 });
    }
  }

  while (frontier.length > 0) {
    const wave = frontier;
    frontier = [];

    for (const { key, direction, depth: currentDepth } of wave) {
      if (visited.has(key)) continue;

      const ent = entitiesByKey.get(key);
      if (!ent) {
        unresolvedSkipped += 1;
        continue;
      }

      visited.add(key);
      closure.add(key);
      if (direction === 'fanIn') {
        fanInClosure.add(key);
      } else {
        fanOutClosure.add(key);
      }

      byDepth[currentDepth] = byDepth[currentDepth] ?? { fanIn: [], fanOut: [] };
      const bucket =
        direction === 'fanIn'
          ? byDepth[currentDepth].fanIn
          : byDepth[currentDepth].fanOut;
      if (!bucket.includes(key)) bucket.push(key);

      if (currentDepth >= depth) continue;

      for (const neighbor of fanInKeys(ent)) {
        if (!neighbor || neighbor === key) continue;
        if (!entitiesByKey.has(neighbor)) {
          unresolvedSkipped += 1;
          continue;
        }
        if (!visited.has(neighbor)) {
          frontier.push({
            key: neighbor,
            direction: 'fanIn',
            depth: currentDepth + 1,
          });
        }
      }
      for (const neighbor of fanOutKeys(ent)) {
        if (!neighbor || neighbor === key) continue;
        if (!entitiesByKey.has(neighbor)) {
          unresolvedSkipped += 1;
          continue;
        }
        if (!visited.has(neighbor)) {
          frontier.push({
            key: neighbor,
            direction: 'fanOut',
            depth: currentDepth + 1,
          });
        }
      }
    }
  }

  return { closure, fanInClosure, fanOutClosure, unresolvedSkipped };
}

export function computeBlastRadius(
  seedEntityKey: string,
  depth: number,
  entitiesByKey: Map<string, ScanCallableEntity>,
  entityKeyToNodeIds: Map<string, string[]>,
  filePathToNodeId: Map<string, string>,
): BlastRadiusResult | null {
  const seed = seedEntityKey.trim();
  if (!seed) return null;

  const naturalMax = computeNaturalMaxDepth(seed, entitiesByKey);
  const effectiveDepth = Math.min(
    Math.max(1, Math.floor(depth)),
    naturalMax.maxDepth,
  );

  const seedEnt = entitiesByKey.get(seed);
  const directFanInKeys = seedEnt ? fanInKeys(seedEnt) : [];
  const directFanOutKeys = seedEnt ? fanOutKeys(seedEnt) : [];

  const byDepth: BlastRadiusByDepth = {
    0: { fanIn: [], fanOut: [] },
  };

  let unresolvedSkipped = 0;
  if (!seedEnt) {
    unresolvedSkipped += 1;
  }

  const expansion = expandUnifiedClosure(
    [seed],
    effectiveDepth,
    entitiesByKey,
    byDepth,
  );

  unresolvedSkipped += expansion.unresolvedSkipped;

  const entityKeys = new Set<string>([seed]);
  expansion.closure.forEach(k => entityKeys.add(k));

  const nodeIds = new Set<string>();
  const seedNodeIds = entityKeyToNodeIds.get(seed) ?? [];
  for (const entityKey of entityKeys) {
    for (const nodeId of entityKeyToNodeIds.get(entityKey) ?? []) {
      nodeIds.add(nodeId);
    }
    const ent = entitiesByKey.get(entityKey);
    const fp = ent?.filePath?.trim();
    if (fp) {
      const fileNodeId = filePathToNodeId.get(fp);
      if (fileNodeId) nodeIds.add(fileNodeId);
    }
  }

  const totalAffected = entityKeys.size - 1;

  return {
    seedEntityKey: seed,
    depth: effectiveDepth,
    maxDepth: naturalMax.maxDepth,
    maxFanInDepth: naturalMax.maxFanInDepth,
    maxFanOutDepth: naturalMax.maxFanOutDepth,
    entityKeys,
    nodeIds,
    seedNodeIds,
    directFanIn: directFanInKeys.filter(k => entitiesByKey.has(k)).length,
    directFanOut: directFanOutKeys.filter(k => entitiesByKey.has(k)).length,
    totalFanIn: expansion.fanInClosure.size,
    totalFanOut: expansion.fanOutClosure.size,
    totalAffected,
    byDepth,
    unresolvedSkipped,
    isLargeClosure: totalAffected > BLAST_RADIUS_LARGE_CLOSURE_THRESHOLD,
  };
}

export function buildEntitiesByKeyFromCallables(
  entities: ScanCallableEntity[],
): Map<string, ScanCallableEntity> {
  const map = new Map<string, ScanCallableEntity>();
  for (const ent of entities) {
    const key = (ent.entityKey ?? '').trim();
    if (key) map.set(key, ent);
  }
  return map;
}

/** Build entity map from full call-graph ``entitiesByFile`` payload. */
export function buildEntitiesByKeyFromEntitiesByFile(
  entitiesByFile: Record<string, ScanCallableEntity[]>,
): Map<string, ScanCallableEntity> {
  const list: ScanCallableEntity[] = [];
  for (const entities of Object.values(entitiesByFile)) {
    list.push(...entities);
  }
  return buildEntitiesByKeyFromCallables(list);
}

function resolveLookupEntityKey(
  identifier: string,
  entitiesByKey: Map<string, ScanCallableEntity>,
): string {
  const trimmed = identifier.trim();
  if (!trimmed) return '';
  if (entitiesByKey.has(trimmed)) return trimmed;

  for (const [entityKey, ent] of entitiesByKey) {
    const entityId = (ent.entityId ?? '').toString().trim();
    const docId = (ent._id ?? '').toString().trim();
    if (trimmed === entityId || trimmed === docId) return entityKey;
  }

  return trimmed;
}

/** Longest fan-in / fan-out chain across multiple seed methods. */
export function computeNaturalMaxDepthForSeeds(
  seedIdentifiers: string[],
  entitiesByKey: Map<string, ScanCallableEntity>,
): NaturalMaxDepth {
  let maxFanInDepth = 0;
  let maxFanOutDepth = 0;
  let maxDepth = 0;

  for (const seed of seedIdentifiers) {
    const lookupKey = resolveLookupEntityKey(seed, entitiesByKey);
    if (!lookupKey) continue;
    const natural = computeNaturalMaxDepth(lookupKey, entitiesByKey);
    maxFanInDepth = Math.max(maxFanInDepth, natural.maxFanInDepth);
    maxFanOutDepth = Math.max(maxFanOutDepth, natural.maxFanOutDepth);
    maxDepth = Math.max(maxDepth, natural.maxDepth);
  }

  return {
    maxFanInDepth,
    maxFanOutDepth,
    maxDepth: Math.max(1, maxDepth),
  };
}

/** Build dropdown options from 1 through natural max depth for a seed. */
export function blastRadiusDepthOptions(maxDepth: number): number[] {
  const cap = Math.max(1, Math.floor(maxDepth));
  return Array.from({ length: cap }, (_, index) => index + 1);
}

export function formatBlastRadiusDepthLabel(
  level: number,
  maxDepth: number,
): string {
  if (level === 1) return 'Depth 1 (direct)';
  if (level === maxDepth && maxDepth > 1) return `Depth ${level} (full)`;
  return `Depth ${level}`;
}
